import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpService} from '../../../common/api/http.service';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {APIResponse, Filter} from '@core/interfaces/system/system-common';
import {
    AbstractLiveModel,
    ChartAssetAlertHistoryResponse,
    ChartModelAlertHistoryResponse,
    ChartModelHistoryResponse,
    FleetLatestStatusResponse,
    LiveAssetRequest,
    LiveFleetRequest,
    LiveModelRequest,
} from '@core/interfaces/live/live';
import {HttpParams} from '@angular/common/http';
import {GuideInfo} from '@theme/components';
import {map} from 'rxjs/operators';
import {LiveConfig} from '@core/interfaces/common/configuration';
import {TableService} from '@core/utils/table.service';

@Injectable()
export class LiveApi {
    private readonly PREFIX_METER_DATA = 'live';
    private readonly PREFIX_OUTPUT = 'live/output';
    private readonly PREFIX_ASSET_MODEL = 'live/output/search';

    constructor(private api: HttpService) {}

    /*
     * Data explorer
     */
    getMeterDataDatasource(): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.PREFIX_METER_DATA}/list`);
    }

    getLiveConfiguration(): Observable<APIResponse<LiveConfig>> {
        return this.api.get(`${this.PREFIX_METER_DATA}/config`);
    }

    /*
     * Generic
     */
    /**
     * Get list of Asset IDs matching search criteria
     *
     * @param assetId Partial search text for assetId
     * @param model Partial search text for model
     * @param asOf filtering time
     * #1023
     */
    getSearchAssetIds(assetId: string, model: string, asOf: string): Observable<APIResponse<string[]>> {
        return this.api.get(`${this.PREFIX_ASSET_MODEL}/asset`, {
            params: new HttpParams()
                .set('assetId', assetId || '')
                .set('model', model || '')
                .set('asOf', asOf || ''),
        });
    }

    /**
     * Get list of models matching search criteria
     *
     * @param assetId Partial search text for assetId
     * @param model Partial search text for model
     * @param asOf filtering time
     * #1023
     */
    getSearchModels(assetId: string, model: string, asOf: string): Observable<APIResponse<string[]>> {
        return this.api.get(`${this.PREFIX_ASSET_MODEL}/model`, {
            params: new HttpParams()
                .set('assetId', assetId || '')
                .set('model', model || '')
                .set('asOf', asOf || ''),
        });
    }

    /*
     * Model, generic
     */
    public getCountResultsByModel(request: LiveModelRequest): Observable<number> {
        return this.api
            .get(`${this.PREFIX_OUTPUT}/model/count`, {
                params: new HttpParams()
                    .set('assetId', request.assetId || '')
                    .set('model', request.model || '')
                    .set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<number>) => res.response));
    }

    getChartModelHistory(request: LiveModelRequest): Observable<APIResponse<ChartModelHistoryResponse>> {
        return this.api.get(`${this.PREFIX_OUTPUT}/model/history`, {
            params: new HttpParams()
                .set('assetId', request.assetId || '')
                .set('model', request.model || '')
                .set('asOf', request.uploadTime || ''),
        });
    }

    getChartModelAlertHistory(
        request: LiveModelRequest,
        groupBy: string,
    ): Observable<APIResponse<ChartModelAlertHistoryResponse>> {
        return this.api.get(`${this.PREFIX_OUTPUT}/model/history-aggregate`, {
            params: new HttpParams()
                .set('assetId', request.assetId || '')
                .set('model', request.model || '')
                .set('asOf', request.uploadTime || '')
                .set('groupBy', groupBy || ''),
        });
    }

    /*
     * Asset
     */
    public getCountResultsByAsset(assetId: string, uploadTime?: string): Observable<number> {
        return this.api
            .get(`${this.PREFIX_OUTPUT}/asset/count`, {
                params: new HttpParams().set('assetId', assetId || '').set('asOf', uploadTime || ''),
            })
            .pipe(map((res: APIResponse<number>) => res.response));
    }

    getChartAssetAlertHistory(request: LiveAssetRequest): Observable<APIResponse<ChartAssetAlertHistoryResponse[]>> {
        return this.api.get(`${this.PREFIX_OUTPUT}/asset/history`, {
            params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
        });
    }

    getAssetLatestModelStatus(id: string, uploadTime: string): Observable<AbstractLiveModel[]> {
        return this.api.get(`${this.PREFIX_OUTPUT}/asset/latest?assetId=${id}&asOf=${uploadTime}`);
    }

    /*
     * Fleet
     */
    public getCountResultsByFleet(uploadTime?: string): Observable<number> {
        return this.api
            .get(`${this.PREFIX_OUTPUT}/fleet/count`, {
                params: new HttpParams().set('asOf', uploadTime || ''),
            })
            .pipe(map((res: APIResponse<number>) => res.response));
    }

    getFleetLatestStatusDetails(request: LiveFleetRequest): Observable<APIResponse<FleetLatestStatusResponse>> {
        return this.api.get(`${this.PREFIX_OUTPUT}/fleet/summary`, {
            params: new HttpParams().set('asOf', request.uploadTime || ''),
        });
    }

    getCurrentAssetAlerts(request: LiveFleetRequest, filters: Filter[] = []): Observable<DataSource> {
        return this.api.postServerDataSource(`${this.PREFIX_OUTPUT}/list-latest?asOf=${request.uploadTime}`, filters);
    }

    //TODO check api
    public getGuideInfo(modelId: string): Observable<GuideInfo[]> {
        return this.api
            .get(`${this.PREFIX_OUTPUT}/guide/${modelId}`)
            .pipe(map((resp: APIResponse<GuideInfo[]>) => resp.response));
    }
}
